import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, SpinningLoader } from "risa-oasis-ui_v2";

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import RpaVersionDialog from "../../../../../components/modals/RpaVersionDialog";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { setSelectedOrganization } from "../../../../../redux/slice/organizationSlice";
import {
  createRpaVersionWithCopy,
  fetchRpaOrganizations,
  fetchRpaVersions,
  setSelectedVersion,
} from "../../../../../redux/slice/rpaConfiguration";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import ProcessSteps from "./ProcessSteps";
import RpaConfigSelect, { SelectOption } from "./RpaConfigSelect";
import "./RpaConfigurationBrowser.scss";

const RpaConfigurationBrowser: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const { organizations, versions, selectedOrganization, selectedVersion } =
    useSelector((state: RootState) => state.rpaConfiguration);

  // Local state
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>("");
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  // Load organizations when component mounts
  useEffect(() => {
    dispatch(fetchRpaOrganizations());
  }, [dispatch]);

  // Convert organizations to dropdown options
  const organizationOptions: SelectOption[] = organizations.data
    ? organizations.data.map((org) => ({
        value: org.id,
        label: org.name || org.id,
      }))
    : [];

  // Convert versions to dropdown options
  const versionOptions: SelectOption[] = versions.data
    ? versions.data.map((version) => ({
        value: version.id,
        label: version.name || version.version || version.id,
      }))
    : [];

  // Handle organization selection
  const handleOrganizationChange = (option: SelectOption) => {
    const organizationId = option.value;
    setSelectedOrganizationId(organizationId);
    setSelectedVersionId(""); // Clear version selection

    if (organizationId) {
      // Find and set the selected organization
      const organization = organizations.data?.find(
        (org) => org.id === organizationId,
      );
      dispatch(setSelectedOrganization(organization?.id || ""));

      // Fetch versions for the selected organization
      dispatch(fetchRpaVersions({ organizationId }));
    } else {
      dispatch(setSelectedOrganization(""));
      dispatch(setSelectedVersion(null));
    }
  };

  // Handle version selection
  const handleVersionChange = (option: SelectOption) => {
    const versionId = option.value;
    setSelectedVersionId(versionId);

    if (versionId) {
      // Find and set the selected version
      const version = versions.data?.find((v) => v.id === versionId);
      dispatch(setSelectedVersion(version || null));
    } else {
      dispatch(setSelectedVersion(null));
    }
  };

  // Handle opening the version creation dialog
  const handleOpenVersionDialog = () => {
    setVersionDialogOpen(true);
  };

  // Handle version creation
  const handleVersionSubmit = async (
    versionId: string,
    sourceVersionId?: string,
  ) => {
    if (!selectedOrganizationId) return;

    try {
      await dispatch(
        createRpaVersionWithCopy({
          organizationId: selectedOrganizationId,
          versionId,
          sourceVersionId,
        }),
      ).unwrap();

      // Select the newly created version
      setSelectedVersionId(versionId);

      // Show success toast
      toast.success(ToastMessages.VERSION_CREATE_SUCCESS);

      // Close dialog
      setVersionDialogOpen(false);
    } catch (error) {
      // Show error toast
      toast.error(ToastMessages.VERSION_CREATE_ERROR);
      throw error; // Re-throw to let dialog handle error state
    }
  };

  if (organizations.loading) {
    return (
      <div className="rpa-browser__loading">
        <SpinningLoader />
        <p>Loading RPA organizations...</p>
      </div>
    );
  }

  if (organizations.error) {
    return (
      <div className="rpa-browser__error">
        <h3>Error Loading Organizations</h3>
        <p>{organizations.error}</p>
        <Button
          onClick={() => dispatch(fetchRpaOrganizations())}
          buttonType="primary"
          size="small"
          disabled={false}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Show no data message when no organizations exist
  if (!organizations.data || organizations.data.length === 0) {
    return (
      <div className="rpa-configuration-browser">
        <div className="rpa-browser__no-data">
          <h3>No RPA Organizations Found</h3>
          <p>
            No RPA organizations are configured yet. Please contact your
            administrator to set up RPA configurations in the Firebase database.
          </p>
          <Button
            onClick={() => dispatch(fetchRpaOrganizations())}
            buttonType="secondary"
            size="medium"
            disabled={organizations.loading}
          >
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rpa-configuration-browser">
      <div className="rpa-browser__controls">
        {/* Organization Dropdown */}
        <div className="rpa-browser__select-section">
          <RpaConfigSelect
            id="organization-select"
            label="Organization"
            options={organizationOptions}
            value={selectedOrganizationId}
            placeholder="Select Organization"
            onChange={handleOrganizationChange}
          />
        </div>

        {/* Version Dropdown */}
        <div className="rpa-browser__select-section">
          <div className="rpa-browser__version-controls">
            <div className="rpa-browser__version-select">
              <RpaConfigSelect
                id="version-select"
                label="Version"
                options={selectedOrganizationId ? versionOptions : []}
                value={selectedVersionId}
                placeholder={
                  selectedOrganizationId
                    ? "Select Version"
                    : "Select Organization First"
                }
                onChange={handleVersionChange}
                disabled={!selectedOrganizationId}
              />
              {versions.loading && (
                <div className="rpa-browser__dropdown-loading">
                  <SpinningLoader />
                  <span>Loading versions...</span>
                </div>
              )}
            </div>
            {selectedOrganizationId && (
              <Button
                onClick={handleOpenVersionDialog}
                buttonType="primary"
                size="small"
                disabled={versions.loading}
              >
                <i className="fas fa-plus"></i>
                Version
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Display Process Steps when version is selected */}
      {selectedVersion &&
        selectedVersion.configuration &&
        Array.isArray(selectedVersion.configuration) && (
          <div className="rpa-browser__process-container">
            <div className="rpa-browser__process-header">
              <h5>Process Configuration</h5>
              <div className="rpa-browser__process-info">
                <span className="rpa-browser__step-count">
                  {selectedVersion.configuration.length} steps
                </span>
                <span className="rpa-browser__version-info">
                  {selectedOrganization?.name || selectedOrganizationId} •{" "}
                  {selectedVersion.name ||
                    selectedVersion.version ||
                    selectedVersionId}
                </span>
              </div>
            </div>
            <ProcessSteps steps={selectedVersion.configuration} />
          </div>
        )}

      {/* Display Selected Data Summary */}
      {selectedOrganization && selectedVersion && (
        <div className="rpa-browser__selected-data">
          <h3>Selected Configuration</h3>
          <div className="rpa-browser__data-card">
            <h4>{selectedOrganization.name || selectedOrganization.id}</h4>
            {selectedOrganization.description && (
              <p>{selectedOrganization.description}</p>
            )}
            <div className="rpa-browser__data-details">
              <strong>Organization ID:</strong> {selectedOrganization.id}
            </div>
          </div>

          {selectedVersion && (
            <div className="rpa-browser__data-card">
              <h4>
                {selectedVersion.name ||
                  selectedVersion.version ||
                  selectedVersion.id}
              </h4>
              {selectedVersion.description && (
                <p>{selectedVersion.description}</p>
              )}
              <div className="rpa-browser__data-details">
                <div>
                  <strong>Version ID:</strong> {selectedVersion.id}
                </div>
                {selectedVersion.version && (
                  <div>
                    <strong>Version:</strong> {selectedVersion.version}
                  </div>
                )}
                {selectedVersion.createdAt && (
                  <div>
                    <strong>Created:</strong> {selectedVersion.createdAt}
                  </div>
                )}
                {selectedVersion.updatedAt && (
                  <div>
                    <strong>Updated:</strong> {selectedVersion.updatedAt}
                  </div>
                )}
                {selectedVersion.configuration &&
                  Array.isArray(selectedVersion.configuration) && (
                    <div>
                      <strong>Process Steps:</strong>{" "}
                      {selectedVersion.configuration.length}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Version Creation Dialog */}
      <RpaVersionDialog
        isOpen={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        onSubmit={handleVersionSubmit}
        organizationName={
          selectedOrganization?.name || selectedOrganizationId || ""
        }
        loading={versions.loading}
        existingVersions={versions.data || []}
      />
    </div>
  );
};

export default RpaConfigurationBrowser;
